import * as React from 'react'

import { Button, List, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { useBulkUpsertSpendingLimits } from 'modules/benefits/lib/useBulkUpsertSpendingLimits'
import type { SpendingLimitOverride } from 'modules/benefits/pages/EmployeesPage/ActivateBenefitsModal/types'

interface BulkResetSpendingLimitModalProps {
  isOpen: boolean
  selectedItems: SpendingLimitOverride[]
  onClose: () => void
  onSave: () => void
  defaultLimits: {
    meals: number
    transportation: number
  }
}

export const BulkResetSpendingLimitModal = ({
  isOpen,
  selectedItems,
  onClose,
  onSave,
  defaultLimits,
}: BulkResetSpendingLimitModalProps) => {
  const { upsertLimits } = useBulkUpsertSpendingLimits()
  const { access } = useCurrentSession()
  const accessLocale = access?.locale

  const handleSave = async () => {
    const overrides = (selectedItems || []).map((item) => ({
      employeeId: item.employeeId,
      category: item.category,
      amountCents:
        item.category === 'meals'
          ? defaultLimits.meals
          : item.category === 'transportation'
          ? defaultLimits.transportation
          : 0,
    }))
    await upsertLimits(overrides, () => {
      onSave()
      onClose()
    })
  }

  const handleClose = () => {
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      title={i18n.t(
        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.reset_limits_title'
      )}
      description={i18n.t(
        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.reset_limits_description'
      )}
      onClose={handleClose}
      onSubmit={handleSave}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <List>
        {defaultLimits.meals > 0 && (
          <List.Item>
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('benefits.homepage.static_benefits.es.meals.title')}:
            </Text.Normal>{' '}
            {i18n.t(
              'benefits.tabs.employees.activation_results.modal.edit_spending_limit.amount_per_month',
              {
                amount: MoneyWithCurrency({
                  amount: defaultLimits.meals,
                  currency: 'EUR',
                  locale: accessLocale,
                }),
              }
            )}
          </List.Item>
        )}
        {defaultLimits.transportation > 0 && (
          <List.Item>
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('benefits.homepage.static_benefits.es.transport.title')}:
            </Text.Normal>{' '}
            {i18n.t(
              'benefits.tabs.employees.activation_results.modal.edit_spending_limit.amount_per_month',
              {
                amount: MoneyWithCurrency({
                  amount: defaultLimits.transportation,
                  currency: 'EUR',
                  locale: accessLocale,
                }),
              }
            )}
          </List.Item>
        )}
      </List>
    </Modal>
  )
}
